import type { Theme } from '@mui/material';
import { Box } from '@mui/material';
import type { ComponentProps } from 'react';
import { useEffect, useRef } from 'react';
import { combineSx } from '@/utils/combineSx';
import { removeElementsById } from './utils/htmlSanitizer';

const htmlWrapperStyles = (theme: Theme) => ({
  width: '100%',
  overflowX: 'auto',
  backgroundColor: theme.palette.baseColors.white.c100,
  '& table': {
    width: '100%',
  },
});

const ELEMENTS_TO_REMOVE = [
  'start',
  'disclaimer',
  'faclitity-productions',
  'facility-productions',
  'end',
];

type ReportHtmlRendererProps = ComponentProps<typeof Box> & {
  html: string;
  shouldCleanDom: boolean;
  onCleanupComplete: () => void;
};

const ReportHtmlRenderer = ({
  html,
  shouldCleanDom,
  onCleanupComplete,
  sx,
  ...props
}: ReportHtmlRendererProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.innerHTML = html;
  }, [html]);

  useEffect(() => {
    if (!shouldCleanDom || typeof document === 'undefined') {
      return;
    }

    removeElementsById(ELEMENTS_TO_REMOVE);
    onCleanupComplete();
  }, [shouldCleanDom, onCleanupComplete]);

  return (
    <Box
      ref={containerRef}
      sx={combineSx(htmlWrapperStyles, sx)}
      data-id='asset-report-html'
      {...props}
    />
  );
};

export { ReportHtmlRenderer };
